import type { CameraMode, RouteGeometry } from "./cameraPlan";
import { type LngLat, routeLengthMeters } from "./cameraRoute";
import { sigmaFramesForMeters, smoothSeries } from "./cameraSpline";
import {
  altitudeToZoom,
  elevationAtFraction,
  viewportGroundSpanMeters,
  zoomToAltitude,
} from "./cameraZoom";

/**
 * TerrainSampler reads the DEM elevation in metres at a [lng, lat], or null when
 * the terrain is not loaded there (off-coverage, or no DEM configured at all). It
 * is injected into the clearance pass so the render-free core owns the clearance
 * math while the map package — the only layer that can read the terrain — supplies
 * the data. A null sample contributes no constraint, so the pass degrades to a
 * no-op when the terrain is absent.
 */
export type TerrainSampler = (lngLat: [number, number]) => number | null;

/**
 * ClearanceFrame is the per-frame camera state the clearance pass reads: the
 * look-at `center` (lng/lat), the framing `zoom`, the `pitch` in degrees (MapLibre
 * convention, 0 = straight down), the `bearing` in degrees, and the `fraction` of
 * route progress the frame sits at (so a missing terrain sample under the centre
 * can fall back to the route's own surface elevation). It is the channel slice the
 * resolver already holds per frame, handed across without copying the whole pose.
 */
export interface ClearanceFrame {
  bearing: number;
  center: LngLat;
  fraction: number;
  pitch: number;
  zoom: number;
}

/**
 * EYE_CLEARANCE_MARGIN is the minimum vertical gap in metres the camera eye keeps
 * above the terrain beneath and around it. The eye is an abstract point with no
 * collision body, so a flat floor of ~120 m keeps it from grazing or sinking into a
 * slope the flight passes over while staying low enough that the trail is not lost.
 * This is the geometric baseline every mode clears by; MODE_EYE_CLEARANCE_SCALE then
 * lifts a floaty mode's resolved clearance above it.
 */
const EYE_CLEARANCE_MARGIN = 120;

/**
 * MODE_EYE_CLEARANCE_SCALE multiplies the clearance LIFT (how far above the look-at
 * the pass raises the eye to clear terrain) per camera mode, so a mode's clearance
 * character is distinct: `drone` floats the highest (a grand aerial wants air beneath
 * it), `helicopter` close behind (its lateral eye hangs over off-corridor terrain),
 * `overview` and `cinematic` a touch higher than the grounded baseline,
 * `chase`/`smart`/`director` at the baseline (their resolved clearance is the pure
 * geometry). The scale only ever
 * raises the eye further (every value is >= 1), so it never under-clears terrain, and
 * it applies to the clearance lift — which is zero on a flat route where no constraint
 * fires — so a flat flight balloons for no mode and stays bit-identical across modes.
 * Applying it to the whole lift (not one sample's margin) makes the float observable
 * even when a sightline sample, not the eye floor, is the binding constraint. Held as
 * a constant table so a mode's clearance character is one literal.
 */
const MODE_EYE_CLEARANCE_SCALE: Record<CameraMode, number> = {
  chase: 1.0,
  cinematic: 1.1,
  director: 1.0,
  drone: 1.4,
  helicopter: 1.3,
  overview: 1.15,
  smart: 1.0,
};

/**
 * RAY_CLEARANCE_MARGIN is the vertical gap in metres the straight eye-to-target
 * sightline (and every frustum sample) keeps above any ridge it crosses. It is
 * tighter than the eye floor because a ridge only needs to not occlude the framed
 * terrain (a near-grazing sightline still reads), whereas the eye itself must stay
 * comfortably airborne.
 */
const RAY_CLEARANCE_MARGIN = 90;

/**
 * SIGHTLINE_SAMPLES is how many interior points the eye→target ray is sampled at to
 * test for an occluding ridge. Eight interior samples (the endpoints are the eye and
 * the target, already constrained) catch a ridge between the camera and the trail
 * without the per-frame cost of a dense march; the coarse resolve-time pass favours
 * a handful of probes over per-metre accuracy. The forward fan, lateral, and
 * foreground samples extend this central march to the rest of the visible frustum.
 */
const SIGHTLINE_SAMPLES = 8;

/**
 * FORWARD_FAN_SAMPLES is how many points the frustum coverage extends PAST the
 * look-at along the heading, so a ridge beyond the trail but still framed cannot
 * clip. The fan sits on the eye→center axis extended, at growing ground distances
 * past the look-at.
 */
const FORWARD_FAN_SAMPLES = 4;

/**
 * FORWARD_STEP_SPAN_FRACTION sizes one forward-fan step as a fraction of the
 * viewport's ground span at the frame's zoom, so the fan reaches as far past the
 * look-at as the framing actually shows at any zoom (a wide aerial reaches further
 * than a tight follow). The four samples sit at 0.5/1/1.5/2 of this step past the
 * look-at, covering the forward depth of the visible frame.
 */
const FORWARD_STEP_SPAN_FRACTION = 0.4;

/**
 * LATERAL_OFFSET_SPAN_FRACTION sizes the perpendicular (±90° off the heading)
 * offset of the frame-edge shoulder samples as a fraction of the viewport's ground
 * span, so the lateral probes track the frame's half-width at any zoom. A shoulder
 * just inside the frame edge is where an off-axis ridge clips that the central ray
 * alone misses.
 */
const LATERAL_OFFSET_SPAN_FRACTION = 0.25;

/**
 * PITCH_SEARCH_STEP_DEG is the granularity the pitch-reduction search steps the
 * camera toward top-down at while hunting the tilt that clears an occluded
 * sightline. A 5° step trades a little precision (the resolved pitch lands within a
 * step of the true clearing angle) for a handful of sightline tests per occluded
 * frame, matching the coarse resolve-time pass that already samples the ray at only
 * SIGHTLINE_SAMPLES points.
 */
const PITCH_SEARCH_STEP_DEG = 5;

/**
 * ZOOM_FLOOR / ZOOM_CEIL bound the clearance-adjusted zoom to the plan schema's
 * zoom range, so a degenerate constraint (a sample far above the camera) can never
 * drive the zoom outside what a CameraShot may carry.
 */
const ZOOM_FLOOR = 0;
const ZOOM_CEIL = 22;

/**
 * MAX_CLEARANCE_WIDEN caps how many zoom levels below the frame's planned zoom the
 * clearance fallback may widen the eye. Pitch reduction is the primary clearance
 * lever and runs first, so a real occluder is mostly cleared by tilting toward
 * top-down; the zoom widening is only the residual fallback for what the tilt floor
 * leaves. Without a cap the residual is unbounded — at a route end the eye sits
 * `altitude·sin(pitch)` behind the trailhead over off-corridor terrain, and the
 * underground-floor / sightline solves there drive the zoom out hard, which the
 * anticipation window then bleeds toward the first and last frames so the flight
 * opens and closes blown out. Five zoom levels (a 32× altitude span) is enough
 * headroom to clear a genuine tall peak the tilt could not while keeping the ends at
 * a proper framing; a deeper occluder is handled by the tilt to PITCH_FLOOR rather
 * than by zooming out past this. The cap is still clamped to the schema range.
 */
const MAX_CLEARANCE_WIDEN = 5;

/**
 * PITCH_FLOOR is the lowest pitch (MapLibre convention, 0 = straight down) the
 * clearance pass will tilt the camera toward to clear an occluded sightline. A
 * tilt toward top-down puts the eye more directly above the look-at point, so a
 * ridge between the camera and the trail can no longer occlude it; the floor at
 * ~20° stops the reframe before the view goes fully overhead, so a shot keeps its
 * mode character and only a real obstruction flattens it. Below this the pass
 * falls back to widening the zoom.
 */
const PITCH_FLOOR = 20;

/**
 * PITCH_CEIL is the upper pitch bound the clearance pass respects, matching
 * MapLibre's tilt ceiling. The pass only ever LOWERS pitch toward top-down, so the
 * ceiling is the pose's own pitch in practice; it is held as a named bound so the
 * clamp reads symmetrically with the floor.
 */
const PITCH_CEIL = 85;

/**
 * MIN_COS_PITCH floors `cos(pitch)` so the altitude↔eye-height inversion stays
 * finite as the pitch approaches the horizon (90° → cos 0). MapLibre caps pitch at
 * 85°, so the eye always has some height above the centre ground, but the floor
 * guards the division regardless of the value handed in.
 */
const MIN_COS_PITCH = 0.05;

const TO_RAD = Math.PI / 180;
const METERS_PER_DEGREE_LAT = 111_320;

/**
 * NULL_DEM_HAZARD_MARGIN is the height in metres added above the best known nearby
 * ground when a frustum sample falls on terrain the DEM has not loaded (a null
 * sample). An unloaded tile is treated as a POSSIBLE ridge rather than as clear air:
 * the sample's terrain is estimated as the higher of the route's own surface at the
 * frame's fraction and any nearby known ground, plus this margin, so the clearance
 * keeps the camera above what could be there. ~150 m is a conservative ridge bump on
 * the scale of the eye and ray margins. The estimate is bypassed only in the genuine
 * no-DEM case (a 2D route with no elevations whose every sample is null), preserving
 * the render-free / embed no-op where there is no terrain to clear at all.
 */
const NULL_DEM_HAZARD_MARGIN = 150;

/**
 * ClearanceOptions tune the clearance pass. `frameCount` and the route length size
 * the anticipation window — a fixed route-DISTANCE, not a wall-clock span, so the
 * lead/lag covers the same stretch of trail regardless of how fast the flight
 * crosses it. `viewportHeightPx` calibrates the zoom↔altitude model to the live
 * canvas (the same value the resolver passes everywhere, defaulting to the
 * render-free nominal). `anticipationMeters` is how far ahead of a peak the
 * reframing begins (and how far past it releases): a windowed minimum over that
 * span pulls the clearing response early so the camera reframes before the ridge
 * rather than snapping at it. `fps` is retained for callers that still hand it in,
 * but the window is distance-driven and does not read it. `mode` selects the
 * per-mode eye-margin scale (MODE_EYE_CLEARANCE_SCALE), so a floaty mode keeps more
 * air beneath the eye; it defaults to the smart/director baseline scale when absent.
 * `sampleStride` decimates the per-frame frustum solve: the pass solves
 * frameClearanceAdjustment only every `sampleStride`-th frame (always including the
 * first and last) and linearly interpolates the raw envelopes across the gaps before
 * the dilation + smoothing tail. It is visually identical to solving every frame
 * because that tail already averages the envelope over tens of frames — far coarser
 * than the metres between adjacent frames — while issuing a fraction of the DEM
 * readbacks. Absent or 1 solves every frame and is bit-identical to an unstrided pass;
 * a larger stride is how a long preview flight resolves without stalling the main
 * thread. clearanceSampleStride derives the stride a preview should pass.
 */
export interface ClearanceOptions {
  anticipationMeters?: number;
  fps?: number;
  mode?: CameraMode;
  sampleStride?: number;
  viewportHeightPx?: number;
}

/**
 * ClearanceAdjustment is the per-frame clearance solution: aligned `zoom` and
 * `pitch` arrays the resolver mins against its mode-smoothed channels. Both are
 * one entry per input frame and only ever move the camera toward clearing the
 * terrain — `zoom` only lowers (widens the eye), `pitch` only lowers (tilts toward
 * top-down). When nothing is occluded both arrays equal the input channels, so the
 * resolver's min is a no-op and an un-cleared track is reproduced exactly.
 */
export interface ClearanceAdjustment {
  pitch: number[];
  zoom: number[];
}

/**
 * MODE_ANTICIPATION_METERS is the per-mode lead/lag the reframing uses when a
 * caller does not override it — how far ahead of a peak the clearing response
 * begins (and how far past it releases), in route metres so the anticipation is
 * duration-invariant like the rest of the smoothing. It moves WHEN a reframe
 * starts, never WHETHER it clears (the margins are untouched): `drone` and
 * `helicopter` begin their reframe nearly half a kilometre early (the floaty
 * aerial foresight), `overview` calm, `smart`/`director` the ~300 m baseline that
 * reads as anticipating rather than reacting, `cinematic` and especially `chase`
 * react late (the grounded, nervous energy of a camera that ducks a ridge at the
 * last moment).
 */
const MODE_ANTICIPATION_METERS: Record<CameraMode, number> = {
  chase: 200,
  cinematic: 220,
  director: 300,
  drone: 480,
  helicopter: 420,
  overview: 380,
  smart: 300,
};

/**
 * MIN_CLEARANCE_SAMPLES_PER_WINDOW is how many solved frames the decimated pass keeps
 * inside one anticipation smoothing window. The window's dilation + Gaussian tail
 * averages the raw envelope over `windowFrames` frames on each side, so a handful of
 * solved frames per window reconstructs the same smoothed curve the per-frame solve
 * produces; six keeps the interpolation error well inside the clearance margins the
 * pass already carries. clearanceSampleStride sizes the stride from this, so the solve
 * density tracks the smoothing rather than the (duration- and fps-dependent) frame
 * count.
 */
const MIN_CLEARANCE_SAMPLES_PER_WINDOW = 6;

/**
 * MAX_CLEARANCE_SOLVES caps the total frustum solves one resolve performs, whatever
 * the smoothing window works out to: a long, high-fps flight (a 120 s / 60 fps track
 * is thousands of frames) is floored to a stride that holds the solve count at or
 * below this, so a preview resolve stays a sub-100 ms main-thread task rather than the
 * multi-second stall an unstrided per-frame solve becomes at that length. ~300 solves
 * is ample for the smoothed envelope at any duration.
 */
const MAX_CLEARANCE_SOLVES = 300;

/**
 * clearanceSampleStride derives the frame stride the decimated clearance pass solves
 * at, tying the solve density to the anticipation smoothing window rather than the
 * frame count. It mirrors resolveClearanceAdjustments' own window math —
 * `windowFrames = round(sigmaFramesForMeters(anticipationMeters, count,
 * routeLengthMeters))` — then sizes the stride to keep MIN_CLEARANCE_SAMPLES_PER_WINDOW
 * solved frames inside each window, floored so the total solves never exceed
 * MAX_CLEARANCE_SOLVES on a long or high-fps flight, floored again at 1 so a short flight
 * solves every frame (stride 1, bit-identical to an unstrided pass), and finally CAPPED at
 * windowFrames so the stride never exceeds the smoothing window — beyond that a window-wide
 * ridge could fall entirely between two solved samples and the decimated preview would clip
 * terrain the full-density export clears. The result is the `sampleStride` a preview resolve
 * hands resolveClearanceAdjustments (through resolveCameraTrack); a record resolve passes 1
 * so the exported artifact is solved at full density.
 */
export const clearanceSampleStride = (
  count: number,
  routeLengthMeters: number,
  options: ClearanceOptions,
): number => {
  const anticipationMeters =
    options.anticipationMeters ?? MODE_ANTICIPATION_METERS[options.mode ?? "smart"];
  const windowFrames = Math.max(
    1,
    Math.round(sigmaFramesForMeters(anticipationMeters, count, routeLengthMeters)),
  );

  // Cap at the smoothing window so at least one frame is solved per window: a coarser
  // stride would let a window-wide ridge fall between samples and clip in the preview.
  return Math.min(
    windowFrames,
    Math.max(
      1,
      Math.floor(windowFrames / MIN_CLEARANCE_SAMPLES_PER_WINDOW),
      Math.ceil(count / MAX_CLEARANCE_SOLVES),
    ),
  );
};

/**
 * resolveClearanceAdjustments returns a clearance-adjusted zoom AND pitch per
 * frame: zoom only ever LOWERS the input (raising/widening the camera), and pitch
 * only ever LOWERS toward top-down (tilting the eye over the target), never the
 * reverse. For each frame it derives the camera eye from the zoom + pitch + bearing
 * (the eye sits behind the look-at point at the pitch offset), then enforces:
 *
 * - an underground floor — the eye must sit at least EYE_CLEARANCE_MARGIN (scaled by
 *   the mode) above the terrain beneath and around it (cleared by widening zoom);
 * - frustum occlusion — every sample across the visible frustum (the central
 *   eye→target ray, a forward fan past the look-at, lateral frame-edge shoulders, and
 *   near-foreground points) must clear its terrain by RAY_CLEARANCE_MARGIN, or the
 *   view clips a mountain in frame. This is cleared by first tilting toward top-down
 *   (down to PITCH_FLOOR), which repositions the eye over the target so a ridge no
 *   longer occludes it; if the floor still cannot clear it, by widening zoom.
 *
 * A frustum sample that falls on unloaded DEM (a null sample) is treated as a
 * possible ridge — its terrain is estimated as the route surface or nearby known
 * ground plus NULL_DEM_HAZARD_MARGIN — so an unloaded tile under-protects nothing.
 * The lone exception is the genuine no-terrain case (a 2D route with no elevations
 * whose every sample is null): no hazard can be estimated there, so no frame is
 * constrained and the existing all-clear short-circuit returns the input verbatim.
 *
 * Each per-frame raw series (zoom and pitch) is dilated by a windowed minimum over
 * the anticipation window — so the reframing starts before a peak and releases
 * after — and Gaussian-smoothed, so neither channel jolts. An all-null sampler over
 * a route with no elevations (no DEM at all) returns the input zoom and pitch
 * unchanged on both channels.
 */
export const resolveClearanceAdjustments = (
  frames: readonly ClearanceFrame[],
  route: RouteGeometry,
  sampler: TerrainSampler,
  options: ClearanceOptions,
): ClearanceAdjustment => {
  const count = frames.length;
  if (count === 0) {
    return { pitch: [], zoom: [] };
  }

  const viewportHeightPx = options.viewportHeightPx;
  const modeScale = MODE_EYE_CLEARANCE_SCALE[options.mode ?? "smart"] ?? 1;
  const rawZoom = new Array<number>(count);
  const rawPitch = new Array<number>(count);
  // Track whether any frame's clearance actually moved a channel off its input.
  // When none did — an all-null sampler over a route with no elevations (no DEM at
  // all), or terrain low enough to clear everything — the dilation+smoothing below
  // would be a no-op that still drifts the values by a float ULP; returning the input
  // channels verbatim instead keeps the no-constraint output bit-identical to an
  // un-cleared track on BOTH zoom and pitch.
  let constrained = false;
  const inputZooms = new Array<number>(count);
  const inputPitches = new Array<number>(count);
  // Fill the input channels for every frame — cheap (no sampler), and read by both the
  // no-op short-circuit below and the interpolation endpoints.
  for (let frame = 0; frame < count; frame += 1) {
    const channel = frames[frame];
    if (!channel) {
      inputZooms[frame] = ZOOM_CEIL;
      inputPitches[frame] = PITCH_CEIL;

      continue;
    }

    inputZooms[frame] = channel.zoom;
    inputPitches[frame] = channel.pitch;
  }

  // Solve the per-frame frustum clearance only at a coarse frame stride (always
  // including the first and last frame so the interpolation brackets the whole grid).
  // The dilation + Gaussian tail below already averages the envelope over tens of
  // frames — far coarser than the metres between adjacent frames — so solving every
  // stride-th frame and interpolating the gaps reconstructs the same smoothed curve at
  // a fraction of the DEM-readback cost. A stride of 1 (the default) solves every
  // frame and interpolates nothing, so the output is bit-identical to an unstrided
  // pass.
  const stride = Math.max(1, Math.floor(options.sampleStride ?? 1));
  const sampleIndices = strideSampleIndices(count, stride);
  const sampledZoom = new Array<number>(sampleIndices.length);
  const sampledPitch = new Array<number>(sampleIndices.length);
  for (let sample = 0; sample < sampleIndices.length; sample += 1) {
    const frame = sampleIndices[sample] ?? 0;
    const channel = frames[frame];
    if (!channel) {
      sampledZoom[sample] = ZOOM_CEIL;
      sampledPitch[sample] = PITCH_CEIL;

      continue;
    }

    const adjustment = frameClearanceAdjustment(
      channel,
      route,
      sampler,
      viewportHeightPx,
      modeScale,
    );
    sampledZoom[sample] = adjustment.zoom;
    sampledPitch[sample] = adjustment.pitch;
    if (adjustment.zoom < channel.zoom || adjustment.pitch < channel.pitch) {
      constrained = true;
    }
  }

  if (!constrained) {
    return { pitch: inputPitches, zoom: inputZooms };
  }

  // Interpolate the raw envelopes from the solved samples: each sampled frame keeps its
  // exact solved value and every interior frame lerps the two bracketing samples, so a
  // stride-1 pass (adjacent samples, no interior frames) reproduces the per-frame solve
  // exactly before the dilation + smoothing tail runs.
  interpolateAcrossSamples(rawZoom, sampleIndices, sampledZoom);
  interpolateAcrossSamples(rawPitch, sampleIndices, sampledPitch);

  // Size the anticipation window in route metres so the lead/lag spans a fixed
  // stretch of trail regardless of the flight's speed, then convert to frame
  // samples the same way the framing channels' σ converts: a windowed minimum over
  // `windowFrames` on each side pulls the clearing response into the frames before
  // and after a peak, so the camera reframes before the ridge rather than snapping
  // to the clearance value only on the single frame the ridge is sampled.
  const anticipationMeters =
    options.anticipationMeters ?? MODE_ANTICIPATION_METERS[options.mode ?? "smart"];
  const windowFrames = Math.max(
    1,
    Math.round(sigmaFramesForMeters(anticipationMeters, count, routeLengthMeters(route.line))),
  );
  const dilatedZoom = windowedMinimum(rawZoom, windowFrames);
  const dilatedPitch = windowedMinimum(rawPitch, windowFrames);

  // Gaussian-smooth each dilated envelope so the rise and fall are gradual. The σ is
  // half the window: wide enough to round the dilation's hard shoulders, tight
  // enough that the envelope still tracks a distinct peak. Both channels share the
  // same window so the zoom-out and the tilt lead the peak together and stay in step.
  return {
    pitch: smoothSeries(dilatedPitch, windowFrames / 2),
    zoom: smoothSeries(dilatedZoom, windowFrames / 2),
  };
};

/**
 * S_BACKSOLVE_MAX caps the ray parameter the eye-height back-solve uses, so the
 * `1 / (1 - s)` lever stays finite as a sample approaches the look-at (s → 1) or
 * sits past it (s ≥ 1). A sample at or beyond the look-at is back-solved as if it
 * were a ridge right in front of the target (s = 0.95), which demands the most
 * clearance — the conservative direction, so a far in-frame peak is never
 * under-cleared by a denominator that would otherwise blow up or flip sign.
 */
const S_BACKSOLVE_MAX = 0.95;

/**
 * FrustumSample is one probe of the visible frustum: the [lng, lat] to read the DEM
 * at, the ray parameter `s` along the eye→target axis the straight-ray height is
 * evaluated at (0 at the eye, 1 at the look-at, >1 past it, <0 in the near
 * foreground behind the eye footprint), and the vertical `margin` the camera must
 * clear this sample by. The eye footprint carries the eye margin; the ray,
 * forward-fan, lateral, and foreground probes carry the ray margin. The per-mode
 * float is applied once to the resolved lift, not to a sample's margin.
 */
interface FrustumSample {
  lngLat: LngLat;
  margin: number;
  s: number;
}

/**
 * scaleLift multiplies a required-eye ASL height's LIFT above the look-at ground by
 * the mode scale, so a floaty mode raises the eye further above the same terrain. The
 * lift is `requiredEye - centerGround`; the scaled height is
 * `centerGround + lift * modeScale`. A null required eye (nothing occludes) passes
 * through as null — no constraint, hence no lift to scale — so a flat route stays
 * unchanged for every mode. A scale of 1 (smart/director) returns the input height.
 */
const scaleLift = (
  requiredEye: number | null,
  centerGround: number,
  modeScale: number,
): number | null => {
  if (requiredEye === null) {
    return null;
  }

  return centerGround + (requiredEye - centerGround) * modeScale;
};

/**
 * frameClearanceAdjustment computes one frame's clearance-adjusted zoom and pitch.
 * It reconstructs the eye geometry from the channel, samples the DEM across the
 * visible frustum (the eye footprint, the eye→target ray, a forward fan past the
 * look-at, lateral frame-edge shoulders, and near-foreground points), and resolves
 * any violated constraint by the cheapest reframe that keeps the most mode
 * character:
 *
 * - an occluded frustum is cleared first by tilting the camera toward top-down (down
 *   to PITCH_FLOOR), which slides the eye over the look-at point so a ridge no longer
 *   crosses the frame;
 * - whatever the tilt floor leaves — a buried eye, or a peak the tilt could not lift
 *   the sightline over — is cleared by widening the zoom, which raises the eye over
 *   the terrain. The widening is bounded by MAX_CLEARANCE_WIDEN.
 *
 * The eye floor uses EYE_CLEARANCE_MARGIN and the sightline/frustum samples use
 * RAY_CLEARANCE_MARGIN; the resolved clearance LIFT (how far the required eye sits
 * above the look-at) is then multiplied by `modeScale`, so a floaty mode floats
 * higher than the grounded baseline. The scale only ever raises the eye, so it never
 * under-clears. Returns the resulting `{ zoom, pitch }`, each clamped to its schema
 * range and never raised above the input channel.
 */
const frameClearanceAdjustment = (
  channel: ClearanceFrame,
  route: RouteGeometry,
  sampler: TerrainSampler,
  viewportHeightPx: number | undefined,
  modeScale: number,
): { pitch: number; zoom: number } => {
  const [, lat] = channel.center;
  const altitude = zoomToAltitude(channel.zoom, lat, viewportHeightPx);

  // The look-at ground height: prefer the live DEM under the centre, fall back to
  // the route's own surface elevation, then to sea level. This is the height the
  // eye is raised above, the far end of the sightline ray, and the best nearby known
  // ground a null sample's hazard estimate leans on.
  const centerSample = sampler(channel.center);
  const centerGround = centerSample ?? elevationAtFraction(route, channel.fraction) ?? 0;
  // The route's own surface at this fraction is the other half of the null-DEM hazard
  // estimate: a null sample is treated as the higher of this and the nearby known
  // ground, plus the hazard margin. Null here (a 2D route) leaves the route floor out
  // of the estimate, so a frame with no known ground at all imposes no constraint —
  // the no-DEM no-op.
  const routeFloor = elevationAtFraction(route, channel.fraction);

  // Resolve the frustum first by tilting toward top-down: find the lowest pitch (down
  // to PITCH_FLOOR) at the frame's altitude whose frustum samples all clear. A clear
  // shot keeps the mode pitch; an occluded one drops to the pitch that clears it, or
  // to the floor when even that is not enough.
  const clearancePitch = pitchClearingFrustum(
    channel,
    altitude,
    centerGround,
    routeFloor,
    centerSample,
    modeScale,
    sampler,
    viewportHeightPx,
  );

  // Recompute the eye geometry at the resolved (possibly lowered) pitch, since the
  // pitch sets both the eye's height above the centre and how far behind the target
  // it sits. The zoom constraint below is evaluated against THIS eye, so a tilt that
  // already cleared the frustum leaves no residual zoom widening to apply.
  const eye = eyeGeometry(channel, clearancePitch, altitude, centerGround);
  const samples = frustumSamples(channel, eye, EYE_CLEARANCE_MARGIN, viewportHeightPx);

  let clearanceZoom = channel.zoom;

  // Whatever the tilt floor could not clear — a buried eye or a peak still poking into
  // the frame at PITCH_FLOOR — is cleared by widening the zoom. Take the max required
  // eye height across every frustum sample (each carrying its own margin and null-DEM
  // hazard estimate), lift it by the mode scale, and convert that altitude to the
  // lower zoom.
  const requiredEye = scaleLift(
    frustumRequiredEyeHeight(samples, eye, centerGround, routeFloor, centerSample, sampler),
    centerGround,
    modeScale,
  );
  if (requiredEye !== null && requiredEye > eye.aslHeight) {
    clearanceZoom = Math.min(
      clearanceZoom,
      zoomForEyeHeight(requiredEye, centerGround, eye.cosPitch, lat, viewportHeightPx),
    );
  }

  // Bound the widening: the zoom fallback may drop at most MAX_CLEARANCE_WIDEN levels
  // below the planned zoom, so a degenerate solve at a route end cannot blow the
  // framing out. A real occluder the cap leaves uncleared is handled by the tilt
  // (already resolved above, down to PITCH_FLOOR), not by zooming out further. When no
  // constraint fired clearanceZoom is still the channel zoom, which is at or above
  // this floor, so the cap leaves the no-constraint output untouched.
  const cappedZoom = Math.max(channel.zoom - MAX_CLEARANCE_WIDEN, clearanceZoom);

  return {
    pitch: Math.max(PITCH_FLOOR, Math.min(PITCH_CEIL, clearancePitch)),
    zoom: Math.max(ZOOM_FLOOR, Math.min(ZOOM_CEIL, cappedZoom)),
  };
};

/**
 * EyeGeometry is the reconstructed camera eye for a trial pitch: its [lng, lat]
 * footprint, its ASL `aslHeight` (centre ground plus the altitude's vertical
 * component), and the `cosPitch` the zoom back-solve reuses. Held as one value so the
 * frustum sampling and the required-eye solve read a single consistent eye.
 */
interface EyeGeometry {
  aslHeight: number;
  cosPitch: number;
  lngLat: LngLat;
}

/**
 * eyeGeometry reconstructs the camera eye for a trial pitch at the frame's altitude.
 * Tilting toward top-down raises the eye (`altitude * cos(pitch)` grows as the pitch
 * falls) and pulls it in over the target (`altitude * sin(pitch)` shrinks), so both
 * the eye height and the footprint move with the pitch. The footprint sits behind the
 * look-at along `bearing + 180` (the eye trails the heading).
 */
const eyeGeometry = (
  channel: ClearanceFrame,
  pitch: number,
  altitude: number,
  centerGround: number,
): EyeGeometry => {
  const pitchRad = pitch * TO_RAD;
  const cosPitch = Math.max(MIN_COS_PITCH, Math.cos(pitchRad));
  const sinPitch = Math.max(0, Math.sin(pitchRad));

  return {
    aslHeight: centerGround + altitude * cosPitch,
    cosPitch,
    lngLat: movePoint(channel.center, channel.bearing + 180, altitude * sinPitch),
  };
};

/**
 * frustumSamples builds the ~18 probe points the clearance tests per frame, covering
 * the visible frustum rather than just the central ray:
 *
 * - the eye footprint (s = 0) carrying the eye margin, so the eye stays airborne over
 *   its own ground;
 * - SIGHTLINE_SAMPLES interior points on the eye→target ray, catching a ridge
 *   between the camera and the trail;
 * - a forward fan of FORWARD_FAN_SAMPLES points past the look-at along the heading,
 *   at growing fractions of the viewport ground span, catching a peak beyond the
 *   trail that rises into frame;
 * - lateral shoulders ±perpendicular to the heading at the centre and ~2/3 along the
 *   ray, catching an off-axis ridge at the frame edge the central ray misses;
 * - two near-foreground points just in front of the eye (s a touch below the first
 *   ray sample), the bottom of the frustum.
 *
 * Every off-ray point is projected onto the eye→target axis for its `s`, so the
 * straight-ray height and the back-solve reuse the same lerp the central ray uses.
 */
const frustumSamples = (
  channel: ClearanceFrame,
  eye: EyeGeometry,
  eyeMargin: number,
  viewportHeightPx: number | undefined,
): FrustumSample[] => {
  const [, lat] = channel.center;
  const span = viewportGroundSpanMeters(channel.zoom, lat, viewportHeightPx);
  const forwardStep = FORWARD_STEP_SPAN_FRACTION * span;
  const lateralOffset = LATERAL_OFFSET_SPAN_FRACTION * span;
  const samples: FrustumSample[] = [];

  // The eye footprint: an s = 0 probe with the eye margin, so the back-solve floors
  // the eye at terrain + eyeMargin (the s = 0 lerp reduces to exactly that).
  samples.push({ lngLat: eye.lngLat, margin: eyeMargin, s: 0 });

  // Interior ray samples from the eye toward the look-at.
  for (let step = 1; step <= SIGHTLINE_SAMPLES; step += 1) {
    const s = step / (SIGHTLINE_SAMPLES + 1);
    samples.push({
      lngLat: pointAlongRay(eye.lngLat, channel.center, s),
      margin: RAY_CLEARANCE_MARGIN,
      s,
    });
  }

  // Two near-foreground points between the eye and the first ray sample, the bottom
  // of the frustum — a touch in front of the eye so a hummock right below the camera
  // is caught.
  const firstRayS = 1 / (SIGHTLINE_SAMPLES + 1);
  for (const frac of [0.33, 0.66]) {
    const s = firstRayS * frac;
    samples.push({
      lngLat: pointAlongRay(eye.lngLat, channel.center, s),
      margin: RAY_CLEARANCE_MARGIN,
      s,
    });
  }

  // Forward fan past the look-at along the heading: ground distances of 0.5/1/1.5/2
  // forward steps beyond the centre, each projected back onto the eye→target axis for
  // its s (> 1). A peak here rises into the upper frame.
  for (let step = 1; step <= FORWARD_FAN_SAMPLES; step += 1) {
    const distance = (step / 2) * forwardStep;
    const point = movePoint(channel.center, channel.bearing, distance);
    samples.push({
      lngLat: point,
      margin: RAY_CLEARANCE_MARGIN,
      s: axisParameter(eye, channel.center, point),
    });
  }

  // Lateral frame-edge shoulders ±perpendicular to the heading, at the look-at and at
  // ~2/3 along the ray, catching an off-axis ridge just inside the frame edge the
  // central ray cannot see.
  for (const along of [1, 2 / 3]) {
    const base = along >= 1 ? channel.center : pointAlongRay(eye.lngLat, channel.center, along);
    for (const side of [90, -90]) {
      const point = movePoint(base, channel.bearing + side, lateralOffset);
      samples.push({
        lngLat: point,
        margin: RAY_CLEARANCE_MARGIN,
        s: axisParameter(eye, channel.center, point),
      });
    }
  }

  return samples;
};

/**
 * pointAlongRay returns the [lng, lat] at fraction `s` from `from` toward `to` (0 at
 * `from`, 1 at `to`), a straight planar interpolation of the two corners.
 */
const pointAlongRay = (from: LngLat, to: LngLat, s: number): LngLat => [
  from[0] + (to[0] - from[0]) * s,
  from[1] + (to[1] - from[1]) * s,
];

/**
 * axisParameter projects an off-ray point onto the eye→target axis and returns its
 * normalised position `s` (0 at the eye footprint, 1 at the look-at). A point beyond
 * the look-at yields s > 1, one behind the eye s < 0. The projection is a planar dot
 * product in local equirectangular metres, exact for the few-kilometre offsets in
 * play. When the eye and the look-at coincide (a top-down frame, zero ground offset)
 * the axis is degenerate and the point is treated as at the look-at (s = 1).
 */
const axisParameter = (eye: EyeGeometry, center: LngLat, point: LngLat): number => {
  const lat = center[1];
  const metersPerDegLng = METERS_PER_DEGREE_LAT * Math.cos(lat * TO_RAD);
  const axisEast = (center[0] - eye.lngLat[0]) * metersPerDegLng;
  const axisNorth = (center[1] - eye.lngLat[1]) * METERS_PER_DEGREE_LAT;
  const axisLengthSquared = axisEast * axisEast + axisNorth * axisNorth;
  if (axisLengthSquared <= 0) {
    return 1;
  }

  const pointEast = (point[0] - eye.lngLat[0]) * metersPerDegLng;
  const pointNorth = (point[1] - eye.lngLat[1]) * METERS_PER_DEGREE_LAT;

  return (pointEast * axisEast + pointNorth * axisNorth) / axisLengthSquared;
};

/**
 * pitchClearingFrustum returns the lowest pitch (toward top-down, but never below
 * PITCH_FLOOR) whose frustum samples all clear at the frame's altitude, or the
 * frame's own pitch when the frustum is already clear. It steps the pitch down from
 * the channel pitch to the floor, returning the first pitch that clears; if none in
 * the range clears it, the floor is returned and the zoom fallback in
 * frameClearanceAdjustment takes over. The clear test uses the mode-scaled lift, so a
 * floaty mode tilts in step with the higher eye it will widen to — the pitch and zoom
 * stay coherent. The search is a coarse descent — a handful of steps is enough at the
 * resolve-time pass's accuracy — so a clear shot exits on the first (its own) pitch
 * with one frustum test.
 */
const pitchClearingFrustum = (
  channel: ClearanceFrame,
  altitude: number,
  centerGround: number,
  routeFloor: number | null,
  nearbyKnownGround: number | null,
  modeScale: number,
  sampler: TerrainSampler,
  viewportHeightPx: number | undefined,
): number => {
  const startPitch = Math.min(PITCH_CEIL, Math.max(0, channel.pitch));
  if (startPitch <= PITCH_FLOOR) {
    return startPitch;
  }

  const steps = Math.max(1, Math.ceil((startPitch - PITCH_FLOOR) / PITCH_SEARCH_STEP_DEG));
  for (let step = 0; step <= steps; step += 1) {
    const pitch = Math.max(PITCH_FLOOR, startPitch - step * PITCH_SEARCH_STEP_DEG);
    const eye = eyeGeometry(channel, pitch, altitude, centerGround);
    const samples = frustumSamples(channel, eye, EYE_CLEARANCE_MARGIN, viewportHeightPx);
    const required = scaleLift(
      frustumRequiredEyeHeight(samples, eye, centerGround, routeFloor, nearbyKnownGround, sampler),
      centerGround,
      modeScale,
    );
    if (required === null || required <= eye.aslHeight) {
      return pitch;
    }
  }

  return PITCH_FLOOR;
};

/**
 * frustumRequiredEyeHeight walks every frustum sample and returns the largest eye ASL
 * height any of them demands, or null when nothing occludes. The sample's parameter
 * s is clamped to [0, S_BACKSOLVE_MAX] for both the occlusion test and the back-solve:
 * a sample at or PAST the look-at (s >= 1) is read against a grazing line just above
 * the look-at rather than against the eye→target ray extended (which descends
 * underground past the target and would flag every patch of ground beyond the trail).
 * The straight-ray height at the clamped s is `lerp(eye.aslHeight, centerGround, s)`.
 * The sample's terrain is the live DEM, or — when the DEM is null there — the null-DEM
 * hazard estimate: the higher of the route surface and the nearby known centre ground
 * plus NULL_DEM_HAZARD_MARGIN, treating an unloaded tile as a possible ridge. The ray
 * must pass at least `margin * (1 - s)` clear above the ground — the full margin at
 * the eye, tapering to nothing at the look-at, because the sightline legitimately
 * converges on the ground it is filming. Where terrain intrudes into that tapered
 * clearance, the eye height that restores it is back-solved from the same lerp
 * (target end pinned at centerGround, only the eye end raised), which reduces to the
 * exactly-grazing eye height plus one full margin; the clamp keeps the `1 / (1 - s)`
 * lever finite so a forward sample is cleared as a near-target ridge. A null sample
 * with no known ground at all (a 2D route, no nearby DEM) contributes nothing — the
 * no-DEM no-op.
 */
const frustumRequiredEyeHeight = (
  samples: readonly FrustumSample[],
  eye: EyeGeometry,
  centerGround: number,
  routeFloor: number | null,
  nearbyKnownGround: number | null,
  sampler: TerrainSampler,
): number | null => {
  let required: number | null = null;
  for (const sample of samples) {
    const ground = sampleGround(sample.lngLat, routeFloor, nearbyKnownGround, sampler);
    if (ground === null) {
      continue;
    }

    // Clamp the parameter to [0, S_BACKSOLVE_MAX] so a sample at or past the look-at
    // (s >= 1) tests and solves as a near-target ridge: the ray-extended height past
    // the target descends underground, which would flag benign ground beyond the
    // trail, so a forward sample is held to a grazing line just above the look-at.
    const s = Math.min(S_BACKSOLVE_MAX, Math.max(0, sample.s));
    const rayHeight = eye.aslHeight + (centerGround - eye.aslHeight) * s;
    // The required clearance tapers with the ray's remaining travel — the full
    // margin at the eye (s = 0), shrinking to nothing at the look-at, because the
    // sightline legitimately CONVERGES ON the ground it is filming. Clear only
    // when the ray passes at least that tapered clearance above the terrain — the
    // same boundary the back-solve below lands on, so a solved frame re-tests as
    // exactly clear and the loop is stable. The sign matters: the previous form
    // tolerated terrain up to a full margin ABOVE the ray before reframing, which
    // let ridges graze — or outright cross — the sightline and read as the camera
    // clipping through mountains.
    const clearance = sample.margin * (1 - s);
    if (ground <= rayHeight - clearance) {
      continue;
    }

    // The ray must pass `clearance` above this terrain at parameter s. With the
    // target end pinned at centerGround, solving `lerp(eye, centerGround, s) =
    // ground + margin * (1 - s)` for the eye end reduces to the exactly-grazing
    // eye height plus one full margin — a uniform lift whatever the sample's s.
    const neededEye = (ground - centerGround * s) / (1 - s) + sample.margin;
    required = required === null ? neededEye : Math.max(required, neededEye);
  }

  return required;
};

/**
 * sampleGround reads the DEM at a frustum sample, or returns the null-DEM hazard
 * estimate when the tile is unloaded: the higher of the route surface at the frame's
 * fraction (`routeFloor`) and the nearby known centre ground (`nearbyKnownGround`,
 * the live DEM under the centre when it loaded), plus NULL_DEM_HAZARD_MARGIN. An
 * unloaded tile is treated as a possible ridge so the camera stays above what could
 * be there. When NEITHER is known — a 2D route with no elevations whose centre DEM is
 * also null — it returns null, imposing no constraint: the genuine no-terrain case
 * the embed/render-free no-op rests on. Note `nearbyKnownGround` is the raw centre
 * SAMPLE (null when unloaded), not the route-fallen-back centre ground, so the no-op
 * is not defeated by the centre's own sea-level fallback.
 */
const sampleGround = (
  lngLat: LngLat,
  routeFloor: number | null,
  nearbyKnownGround: number | null,
  sampler: TerrainSampler,
): number | null => {
  const sampled = sampler(lngLat);
  if (sampled !== null) {
    return sampled;
  }

  if (routeFloor === null && nearbyKnownGround === null) {
    return null;
  }

  const known = Math.max(
    routeFloor ?? Number.NEGATIVE_INFINITY,
    nearbyKnownGround ?? Number.NEGATIVE_INFINITY,
  );

  return known + NULL_DEM_HAZARD_MARGIN;
};

/**
 * zoomForEyeHeight converts a required eye ASL height to the MapLibre zoom that
 * places the eye there, given the centre ground height and the pitch. The eye's
 * height above the centre ground is `altitude * cos(pitch)`, so the altitude that
 * yields the required height is `(eyeHeight - centerGround) / cos(pitch)`; that
 * altitude maps to a zoom through altitudeToZoom (which clamps to a flight range).
 * A non-positive height-above-ground would mean the eye is at or below the centre
 * ground — there is no usable altitude, so the schema zoom floor is returned.
 */
const zoomForEyeHeight = (
  eyeHeight: number,
  centerGround: number,
  cosPitch: number,
  latitude: number,
  viewportHeightPx: number | undefined,
): number => {
  const altitude = (eyeHeight - centerGround) / cosPitch;
  if (!Number.isFinite(altitude) || altitude <= 0) {
    return ZOOM_FLOOR;
  }

  return altitudeToZoom(altitude, latitude, viewportHeightPx);
};

/**
 * movePoint returns the [lng, lat] reached by travelling `meters` from `origin`
 * along the compass `bearing` (degrees, 0 = north, clockwise), on a local flat-
 * earth approximation. The clearance offsets are at most a few kilometres, so the
 * planar step is accurate to well within a DEM texel and avoids the cost of a
 * great-circle solve per sample.
 */
const movePoint = (origin: LngLat, bearing: number, meters: number): LngLat => {
  if (meters === 0) {
    return origin;
  }

  const bearingRad = bearing * TO_RAD;
  const dNorth = Math.cos(bearingRad) * meters;
  const dEast = Math.sin(bearingRad) * meters;
  const metersPerDegreeLng = METERS_PER_DEGREE_LAT * Math.cos(origin[1] * TO_RAD);
  const dLat = dNorth / METERS_PER_DEGREE_LAT;
  const dLng = metersPerDegreeLng > 0 ? dEast / metersPerDegreeLng : 0;

  return [origin[0] + dLng, origin[1] + dLat];
};

/**
 * windowedMinimum returns, for each index, the minimum over the inclusive window
 * `[i - radius, i + radius]`. It is the dilation that gives the clearance its
 * anticipation: a low value (a wide, terrain-clearing camera) at one frame is
 * pulled into the `radius` frames on either side, so the widening leads the peak
 * and lags it rather than snapping on the single sampled frame. A radius of 0
 * returns a copy unchanged.
 */
const windowedMinimum = (values: readonly number[], radius: number): number[] => {
  const length = values.length;
  const out = new Array<number>(length);
  for (let i = 0; i < length; i += 1) {
    const from = Math.max(0, i - radius);
    const to = Math.min(length - 1, i + radius);
    let min = Number.POSITIVE_INFINITY;
    for (let j = from; j <= to; j += 1) {
      const value = values[j];
      if (value !== undefined && value < min) {
        min = value;
      }
    }

    out[i] = Number.isFinite(min) ? min : (values[i] ?? ZOOM_CEIL);
  }

  return out;
};

/**
 * strideSampleIndices returns the ascending frame indices the decimated clearance
 * solve runs at: every `stride`-th frame from 0, always closed with the last frame
 * (`count - 1`) so the interpolation brackets the whole grid at both ends. A stride of
 * 1 returns every index; a count of 0 returns empty.
 */
const strideSampleIndices = (count: number, stride: number): number[] => {
  const indices: number[] = [];
  for (let frame = 0; frame < count; frame += stride) {
    indices.push(frame);
  }

  const last = count - 1;
  if (last >= 0 && indices[indices.length - 1] !== last) {
    indices.push(last);
  }

  return indices;
};

/**
 * interpolateAcrossSamples fills `out` from the values solved at `indices`: each
 * sampled index takes its solved value exactly and every interior frame between two
 * consecutive samples lerps the bracketing values. Pinning the exact solved value at
 * each sample (rather than letting a lerp land on it) is what keeps a stride-1 pass
 * bit-identical to a per-frame solve — `a + (b - a) * 1` can drift by a float ULP, so
 * only the strictly-interior frames are interpolated. `out` is sized for the full
 * frame grid; a same-length `indices`/`values` pair is walked in lockstep.
 */
const interpolateAcrossSamples = (
  out: number[],
  indices: readonly number[],
  values: readonly number[],
): void => {
  for (let sample = 0; sample < indices.length; sample += 1) {
    out[indices[sample] ?? 0] = values[sample] ?? 0;
  }

  for (let sample = 0; sample < indices.length - 1; sample += 1) {
    const fromIndex = indices[sample] ?? 0;
    const toIndex = indices[sample + 1] ?? fromIndex;
    const fromValue = values[sample] ?? 0;
    const toValue = values[sample + 1] ?? fromValue;
    const span = toIndex - fromIndex;
    for (let frame = fromIndex + 1; frame < toIndex; frame += 1) {
      const t = span > 0 ? (frame - fromIndex) / span : 0;
      out[frame] = fromValue + (toValue - fromValue) * t;
    }
  }
};
